<template>
  <el-dropdown trigger="click" :class="dropdownClass">
    <span class="user-menu-trigger">
      <component :is="getIcon('user')" class="icon" :size="16" />
      <span class="username">{{ props.username }}</span>
      <component :is="getIcon('chevron-down')" class="icon" :size="12" />
    </span>
    <template #dropdown>
      <el-dropdown-menu>
        <!-- Profile -->
        <el-dropdown-item @click="goToProfile">
          <component :is="getIcon('user')" class="menu-icon" :size="16" />
          {{ $t('user.profile') }}
        </el-dropdown-item>

        <!-- My shared documents -->
        <el-dropdown-item data-testid="shared-documents" @click="goToSharedDocuments">
          <Share2Icon class="menu-icon" :size="16" />
          {{ $t('user.sharedDocuments') }}
        </el-dropdown-item>

        <!-- Theme cycle: Auto → Light → Dark → Auto -->
        <el-dropdown-item divided @click="toggleTheme">
          <component :is="themeIcon" class="menu-icon" :size="16" />
          {{ $t(`user.theme.${theme}`) }}
        </el-dropdown-item>

        <!-- Admin Organizations (includes org admin + team admin orgs) -->
        <template v-if="hasAdminOrganizations">
          <el-dropdown-item
            v-for="(org, index) in allAdminOrganizations"
            :key="org.id"
            :divided="index == 0"
            @click="selectOrganization(org)"
          >
            <BuildingIcon class="menu-icon" :size="16" />
            {{ org.name }}
          </el-dropdown-item>
        </template>

        <el-dropdown-item divided>
          <a :href="props.logoutUrl" data-testid="logout" class="logout-link">
            <component :is="getIcon('log-out')" class="menu-icon" :size="16" />
            {{ $t('auth.logout') }}
          </a>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElDropdown, ElDropdownMenu, ElDropdownItem } from 'element-plus'
import { useIcons } from '@/composables/useIcons'
import { useTheme } from '@/composables/useTheme'
import { useAuthStore } from '@/stores/auth'
import { BuildingIcon, MonitorIcon, SunIcon, MoonIcon, Share2Icon } from 'lucide-vue-next'

const props = defineProps<{
  username: string
  logoutUrl: string
}>()

const { getIcon } = useIcons()
const authStore = useAuthStore()
const router = useRouter()
const { theme, toggleTheme } = useTheme()

const themeIcon = computed(() => {
  if (theme.value === 'light') return SunIcon
  if (theme.value === 'dark') return MoonIcon
  return MonitorIcon
})

const dropdownClass = computed(() => {
  return `user-menu user-menu`
})

// Get all unique organizations where user is admin or team admin
const allAdminOrganizations = computed(() => {
  const orgs = new Map<string, { id: string; name: string; isOrgAdmin: boolean }>()

  // Add organizations where user is org admin
  authStore.adminOrganizations?.forEach(org => {
    orgs.set(org.id, { ...org, isOrgAdmin: true })
  })

  // Add organizations where user is team admin (deduplicated)
  authStore.user?.adminTeams?.forEach(team => {
    if (!orgs.has(team.organizationId)) {
      orgs.set(team.organizationId, {
        id: team.organizationId,
        name: team.organizationName,
        isOrgAdmin: false
      })
    }
  })

  return Array.from(orgs.values())
})

const hasAdminOrganizations = computed(() => {
  return allAdminOrganizations.value.length > 0
})

const selectOrganization = (org: { id: string; name: string; isOrgAdmin: boolean }) => {
  if (org.isOrgAdmin) {
    // Org admins go to the full org admin layout
    router.push({ name: 'OrgAdminLayout', params: { orgId: org.id } })
  } else {
    // Team admins go to the team admin layout
    router.push({ name: 'TeamAdminLayout', params: { orgId: org.id } })
  }
}

const goToProfile = () => {
  router.push({ name: 'AccountSettings' })
}

const goToSharedDocuments = () => {
  router.push({ name: 'ArtifactLibrary' })
}

</script>

<style scoped>
.user-menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: white;
}

.user-menu-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-menu-trigger .icon {
  flex-shrink: 0;
}

.user-menu-trigger .username {
  font-size: 14px;
  font-weight: 500;
}

.user-menu-white .user-menu-trigger {
  color: var(--color-on-surface);
  background-color: var(--color-surface-lowest);
  border: 1px solid var(--color-outline-variant);
}

/* Menu item styles */
.menu-icon {
  margin-right: 8px;
  vertical-align: middle;
}

.coming-soon-tag {
  margin-left: 8px;
}

.logout-link {
  display: flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
  width: 100%;
}

.logout-link:hover {
  color: inherit;
}
</style>